import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  LogOut, 
  Search,
  Calendar,
  User
} from 'lucide-react';
import BlogLayout from './BlogLayout';
import useAdminPosts from '../../hooks/useAdminPosts';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate, calculateReadingTime } from '../../utils/blogUtils';

const AdminDashboard = ({ isMenuOpen, setIsMenuOpen }) => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, loading: authLoading } = useAuth();
  const {
    posts,
    deletePost,
    updatePost,
    loadAdminPosts,
    loading,
    error
  } = useAdminPosts();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPreloader, setShowPreloader] = useState(true);

  // Hide preloader when auth loading is complete
  useEffect(() => {
    if (!authLoading) {
      // Small delay to ensure smooth transition
      const timer = setTimeout(() => {
        setShowPreloader(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [authLoading]);

  // Check authentication and load admin posts
  useEffect(() => {
    // Only proceed if auth loading is complete
    if (authLoading) {
      return;
    }
    
    if (!isAuthenticated) {
      navigate('/blog/admin/login');
      return;
    }
    
    // Only load posts if we have a valid session
    const sessionId = localStorage.getItem('adminSessionId');
    if (sessionId) {
      loadAdminPosts();
    } else {
      navigate('/blog/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]); // Add authLoading to dependencies

  // Filter posts based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPosts(posts);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = posts.filter(post =>
        post.title.toLowerCase().includes(query) ||
        post.excerpt.toLowerCase().includes(query) ||
        post.tags.some(tag => tag.toLowerCase().includes(query))
      );
      setFilteredPosts(filtered);
    }
  }, [posts, searchQuery]);

  const handleLogout = () => {
    logout();
    navigate('/blog');
  };

  const handleDeletePost = async (postId) => {
    const result = await deletePost(postId);
    if (result.success) {
      setDeleteConfirm(null);
    }
  };

  const handleTogglePublished = async (post) => {
    const result = await updatePost(post.id, {
      ...post,
      published: !post.published
    });
    
    if (!result.success) {
      console.error('Failed to update post:', result.errors);
    }
  };

  const stats = {
    total: posts.length,
    published: posts.filter(p => p.published).length,
    drafts: posts.filter(p => !p.published).length
  };

  // Don't render anything if not authenticated and auth loading is complete
  if (!authLoading && !isAuthenticated) {
    return null;
  }

  return (
    <>
      {/* Preloader overlay - shown by default, hidden when auth resolves */}
      {showPreloader && (
        <div className="admin-preloader">
          <div className="admin-preloader-content">
            <div className="admin-preloader-spinner"></div>
          </div>
        </div>
      )}

      {/* Main dashboard content - only show if authenticated */}
      {!authLoading && isAuthenticated && (
        <>
          {loading ? (
            <BlogLayout 
              title="Admin Dashboard" 
              isMenuOpen={isMenuOpen} 
              setIsMenuOpen={setIsMenuOpen}
            >
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            </BlogLayout>
          ) : (
            <BlogLayout 
              title="Admin Dashboard"
              description="Blog administration panel"
              isMenuOpen={isMenuOpen} 
              setIsMenuOpen={setIsMenuOpen}
            >
              <div className="admin-container">
                {/* Header */}
                <div className="admin-header mb-8">
                  <div>
                    <h1 className="admin-title">Dashboard</h1>
                  </div>
                  <div className="admin-actions">
                    <Link to="/blog/admin/editor" className="admin-btn">
                      <Plus size={18} />
                      New Post
                    </Link>
                    <button onClick={handleLogout} className="admin-btn secondary">
                      <LogOut size={18} />
                      Logout
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="form-error mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
                    {error}
                  </div>
                )}

                {/* Stats - Compact and subtle */}
                <div className="flex flex-wrap items-center gap-4 mb-8 p-4 bg-slate-800/30 rounded-lg border border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Total:</span>
                    <span className="text-lg font-semibold text-indigo-400">{stats.total}</span>
                  </div>
                  <div className="w-px h-6 bg-slate-600"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Published:</span>
                    <span className="text-lg font-semibold text-green-400">{stats.published}</span>
                  </div>
                  <div className="w-px h-6 bg-slate-600"></div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Drafts:</span>
                    <span className="text-lg font-semibold text-yellow-400">{stats.drafts}</span>
                  </div>
                </div>

                {/* Search - Full width with better styling */}
                <div className="mb-8">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search posts by title, excerpt, or tags..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full form-input pl-12 pr-4 py-3 text-base bg-slate-800/50 border-slate-600/50 focus:border-indigo-500/50 focus:bg-slate-800/80 transition-all duration-200"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                        title="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  {searchQuery && (
                    <p className="mt-2 text-sm text-slate-400">
                      Found {filteredPosts.length} post{filteredPosts.length !== 1 ? 's' : ''} matching "{searchQuery}"
                    </p>
                  )}
                </div>

                {/* Posts Table */}
                <div className="blog-form overflow-hidden">
                  {filteredPosts.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full table-fixed">
                        <thead>
                          <tr className="border-b border-slate-700/70">
                            <th className="w-2/5 text-left py-3 px-6 font-medium text-slate-300 text-xs uppercase tracking-wider">
                              Blog Details
                            </th>
                            <th className="w-1/6 text-left py-3 px-6 font-medium text-slate-300 text-xs uppercase tracking-wider">
                              Status
                            </th>
                            <th className="w-1/6 text-left py-3 px-6 font-medium text-slate-300 text-xs uppercase tracking-wider">
                              Created
                            </th>
                            <th className="w-1/6 text-center py-3 px-6 font-medium text-slate-300 text-xs uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                          {filteredPosts.map((post) => (
                            <tr
                              key={post.id}
                              className="hover:bg-slate-800/20 transition-all duration-200 group"
                            >
                              {/* Title Column */}
                              <td className="py-4 px-6 align-top">
                                <div className="space-y-1.5">
                                  <h3 className="font-semibold text-white text-base leading-tight line-clamp-2 group-hover:text-indigo-300 transition-colors">
                                    {post.title}
                                  </h3>
                                  <p className="text-sm text-slate-400 leading-relaxed line-clamp-2">
                                    {post.excerpt?.slice(0, 80)}
                                    {post.excerpt?.length > 80 && "..."}
                                  </p>
                                  {post.tags && post.tags.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mt-2">
                                      {post.tags.slice(0, 3).map((tag, index) => (
                                        <span
                                          key={index}
                                          className="inline-block px-2 py-0.5 text-xs bg-slate-700/50 text-slate-300 rounded-md"
                                        >
                                          {tag}
                                        </span>
                                      ))}
                                      {post.tags.length > 3 && (
                                        <span className="text-xs text-slate-500">+{post.tags.length - 3}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </td>

                              {/* Status Column */}
                              <td className="py-4 px-6 align-top">
                                <button
                                  onClick={() => handleTogglePublished(post)}
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                                    post.published
                                      ? "bg-green-500/15 text-green-400 border border-green-500/25 hover:bg-green-500/25"
                                      : "bg-yellow-500/15 text-yellow-400 border border-yellow-500/25 hover:bg-yellow-500/25"
                                  }`}
                                >
                                  {post.published ? (
                                    <>
                                      <Eye size={12} />
                                      <span>Live</span>
                                    </>
                                  ) : (
                                    <>
                                      <EyeOff size={12} />
                                      <span>Draft</span>
                                    </>
                                  )}
                                </button>
                              </td>

                              {/* Date Column */}
                              <td className="py-4 px-6 align-top">
                                <div className="text-sm text-slate-400">
                                  <div className="flex items-center gap-1.5">
                                    <Calendar size={12} className="text-slate-500 flex-shrink-0" />
                                    <span>{formatDate(post.created_at)}</span>
                                  </div>
                                </div>
                              </td>

                              {/* Actions Column */}
                              <td className="py-4 px-6 align-top">
                                <div className="flex items-center justify-center gap-1">
                                  {post.published && (
                                    <Link
                                      to={`/blog/${post.slug}`}
                                      target="_blank"
                                      className="p-2 text-slate-400 hover:text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-all duration-200"
                                      title="View Post"
                                    >
                                      <Eye size={14} />
                                    </Link>
                                  )}
                                  <Link
                                    to={`/blog/admin/editor/${post.id}`}
                                    className="p-2 text-slate-400 hover:text-blue-400 hover:bg-blue-500/10 rounded-md transition-all duration-200"
                                    title="Edit Post"
                                  >
                                    <Edit size={14} />
                                  </Link>
                                  <button
                                    onClick={() => setDeleteConfirm(post.id)}
                                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-all duration-200"
                                    title="Delete Post"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state py-12">
                      <h3 className="empty-state-title">
                        {searchQuery ? "No Posts Found" : "No Posts Yet"}
                      </h3>
                      <p className="mb-6">
                        {searchQuery
                          ? "Try adjusting your search criteria."
                          : "Create your first blog post to get started."}
                      </p>
                      {!searchQuery && (
                        <Link to="/blog/admin/editor" className="admin-btn">
                          <Plus size={20} />
                          Create First Post
                        </Link>
                      )}
                    </div>
                  )}
                </div>

                {/* Delete Confirmation Modal */}
                {deleteConfirm && (
                  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="blog-form max-w-md w-full">
                      <h3 className="text-xl font-bold text-white mb-4">Delete Post</h3>
                      <p className="text-slate-300 mb-6">
                        Are you sure you want to delete this post? This action cannot be undone.
                      </p>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => setDeleteConfirm(null)}
                          className="admin-btn secondary"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleDeletePost(deleteConfirm)}
                          className="admin-btn danger"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </BlogLayout>
          )}
        </>
      )}
    </>
  );
};

export default AdminDashboard;